import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import * as metadataService from "../../../services/metadataService";

/**
 * The polymer(s) of a solution. The contributor first says what it is:
 *  - a single polymer (one homopolymer),
 *  - a copolymer (one polymer chain of two or more monomers, with its composition),
 *  - a blend (two or more separate polymers, each with its share in wt%).
 * A polymer that is not in the list can be defined here ("newPolymer"). It is
 * reviewed by an administrator; until then the run is saved as Needs update.
 */
const MODES = [
    { key: "single", icon: "bi-circle", label: "Single polymer", hint: "One homopolymer, e.g. PAN" },
    { key: "copolymer", icon: "bi-link-45deg", label: "Copolymer", hint: "One polymer of 2+ monomers, e.g. PLGA 75:25" },
    { key: "blend", icon: "bi-intersect", label: "Polymer blend", hint: "2+ polymers mixed, e.g. PCL + gelatin" },
];

const ARCHITECTURES = [
    { value: "", label: "Not stated" },
    { value: "RANDOM", label: "Random (statistical)" },
    { value: "ALTERNATING", label: "Alternating" },
    { value: "BLOCK", label: "Block" },
    { value: "GRAFT", label: "Graft" },
];

const DEFINE_NEW = "__define_new__";

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '38px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#adb5bd' }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 })
};

const isEmpty = (v) => v === "" || v === null || v === undefined;

const createEmptyPolymer = () => ({
    polymerName: "",
    polymerWeight: null,
    polymerWeightUnit: "gram",
    molecularWeight: null,
    molecularWeightUnit: "kDa",
    weightRatio: null,
    weightRatioUnit: "wt%",
});

const newDefinition = (polymerType) => ({
    polymerType,
    architecture: "",
    monomers: [],
    fullName: "",
    casNumber: "",
    note: "",
});

export default function PolymerModal({ polymerList, onClose, onSave }) {
    const [polymerInfo, setPolymerInfo] = useState({}); // name -> { polymerType, architecture, monomers }
    const [monomerOptions, setMonomerOptions] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [mode, setMode] = useState(null);
    const [polymers, setPolymers] = useState([createEmptyPolymer()]);
    const [errors, setErrors] = useState([]);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [polymerData, monomerData] = await Promise.all([
                    metadataService.getAllPolymers(),
                    metadataService.getAllMonomers().catch(() => []),
                ]);
                setPolymerInfo(Object.fromEntries((polymerData || []).map(p => [p.polymerName, p])));
                setMonomerOptions((monomerData || [])
                    .map(m => ({ value: m.abbreviation, label: m.name ? `${m.abbreviation} · ${m.name}` : m.abbreviation }))
                    .sort((a, b) => a.value.localeCompare(b.value)));
            } catch (err) {
                console.error("Failed to load polymers:", err);
            } finally {
                setLoaded(true);
            }
        };
        load();
    }, []);

    useEffect(() => {
        const initial = Array.isArray(polymerList) && polymerList.length > 0 ? polymerList : [createEmptyPolymer()];
        setPolymers(initial.length <= 1
            ? initial.map(p => ({ ...p, weightRatio: 100 }))
            : initial.map(p => ({ ...p, weightRatio: p.weightRatio ?? "" })));
        setErrors([]);
        setMode(null); // decided below once the polymer list is known
    }, [polymerList]);

    /** True when the component is (or defines) a copolymer. */
    const isCopolymer = useCallback((p) => {
        if (p.newPolymer) return p.newPolymer.polymerType === "COPOLYMER";
        if (polymerInfo[p.polymerName]) return polymerInfo[p.polymerName].polymerType === "COPOLYMER";
        return !!p.copolymer;
    }, [polymerInfo]);

    // An existing entry opens in the mode it was made in.
    useEffect(() => {
        if (mode !== null || !loaded) return;
        if (polymers.length > 1) setMode("blend");
        else setMode(polymers[0] && isCopolymer(polymers[0]) ? "copolymer" : "single");
    }, [mode, loaded, polymers, isCopolymer]);

    const allNames = useMemo(() => Object.keys(polymerInfo), [polymerInfo]);

    const optionsFor = (currentMode) => {
        const entries = Object.values(polymerInfo)
            .filter(p => currentMode === "blend"
                || (currentMode === "copolymer" ? p.polymerType === "COPOLYMER" : p.polymerType !== "COPOLYMER"))
            .sort((a, b) => a.polymerName.localeCompare(b.polymerName))
            .map(p => ({
                value: p.polymerName,
                label: p.polymerType === "COPOLYMER"
                    ? `${p.polymerName} · copolymer${p.monomers?.length ? ` (${p.monomers.join(':')})` : ''}`
                    : p.fullName && p.fullName !== p.polymerName ? `${p.polymerName} · ${p.fullName}` : p.polymerName,
            }));
        const define = {
            value: DEFINE_NEW,
            label: currentMode === "copolymer" ? "＋ Not in the list? Define a new copolymer"
                : "＋ Not in the list? Define a new polymer",
        };
        return [define, ...entries];
    };

    const clearError = (index, key) => {
        setErrors(prev => {
            const updated = [...prev];
            if (updated[index]) updated[index] = { ...updated[index], [key]: null };
            return updated;
        });
        setFormError(null);
    };

    const updatePolymer = (index, changes) => {
        setPolymers(prev => prev.map((p, i) => (i === index ? { ...p, ...changes } : p)));
    };

    const handleNumberChange = (index, e) => {
        const { name, value } = e.target;
        updatePolymer(index, { [name]: value === "" ? "" : parseFloat(value) });
        clearError(index, name);
    };

    const choosePolymer = (index, value) => {
        if (value === DEFINE_NEW) {
            const type = mode === "copolymer" ? "COPOLYMER" : mode === "single" ? "HOMOPOLYMER" : "COPOLYMER";
            updatePolymer(index, { polymerName: "", newPolymer: newDefinition(type), composition: null, compositionUnit: "mol%", copolymer: type === "COPOLYMER", pendingApproval: undefined });
        } else {
            // A different polymer has different monomers: start its composition afresh
            updatePolymer(index, { polymerName: value || "", newPolymer: undefined, composition: null, compositionUnit: "mol%", copolymer: polymerInfo[value]?.polymerType === "COPOLYMER", pendingApproval: undefined });
        }
        clearError(index, "polymerName");
    };

    const updateDefinition = (index, changes) => {
        setPolymers(prev => prev.map((p, i) => {
            if (i !== index) return p;
            const newPolymer = { ...p.newPolymer, ...changes };
            const next = { ...p, newPolymer, copolymer: newPolymer.polymerType === "COPOLYMER" };
            // Keep the composition in step with the monomers being defined
            if (changes.monomers || changes.polymerType) {
                const monomers = newPolymer.polymerType === "COPOLYMER" ? newPolymer.monomers : [];
                next.composition = (p.composition || []).filter(f => monomers.includes(f.monomer));
            }
            return next;
        }));
        clearError(index, "definition");
    };

    /** The monomers of a copolymer (in composition order), or null for a homopolymer. */
    const monomersOf = (p) => {
        if (p.newPolymer) return p.newPolymer.polymerType === "COPOLYMER" ? (p.newPolymer.monomers || []) : null;
        const info = polymerInfo[p.polymerName];
        return info?.polymerType === "COPOLYMER" ? (info.monomers || []) : null;
    };

    const fractionOf = (polymer, monomer) =>
        (polymer.composition || []).find(f => f.monomer === monomer)?.fraction ?? "";

    const handleCompositionChange = (index, monomer, value) => {
        setPolymers(prev => prev.map((p, i) => {
            if (i !== index) return p;
            const monomers = monomersOf(p) || [];
            return {
                ...p,
                composition: monomers.map(m => ({
                    monomer: m,
                    fraction: m === monomer ? (value === "" ? "" : parseFloat(value)) : fractionOf(p, m),
                })),
                compositionUnit: p.compositionUnit || "mol%",
            };
        }));
        clearError(index, "composition");
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const changeMode = (next) => {
        if (next === mode) return;
        if (next !== "blend" && polymers.filter(p => p.polymerName).length > 1
            && !window.confirm("Keep only the first polymer and remove the others from this setup?")) {
            return;
        }
        setFormError(null);
        setErrors([]);
        if (next === "blend") {
            const list = polymers.length >= 2 ? polymers : [...polymers, createEmptyPolymer()];
            setPolymers(list.map(p => ({ ...p, weightRatio: "" })));
        } else {
            // One polymer: keep the first, if it is of the chosen kind
            const first = polymers[0] || createEmptyPolymer();
            const named = first.polymerName || first.newPolymer;
            const fits = next === "copolymer" ? isCopolymer(first) : !isCopolymer(first);
            setPolymers([named && fits ? { ...first, weightRatio: 100 } : { ...createEmptyPolymer(), weightRatio: 100 }]);
        }
        setMode(next);
    };

    const handleAddPolymer = () => {
        setPolymers(prev => [...prev, createEmptyPolymer()].map(p => ({ ...p, weightRatio: p.weightRatio ?? "" })));
        setErrors(prev => [...prev, {}]);
    };

    const handleRemovePolymer = (index) => {
        setPolymers(prev => prev.filter((_, i) => i !== index));
        setErrors(prev => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
        const newErrors = [];
        let totalWeightRatio = 0;
        let hasWeightRatioError = false;
        const isBlend = mode === "blend";
        const lowerNames = allNames.map(n => n.toLowerCase());

        polymers.forEach((p) => {
            const err = {};
            const def = p.newPolymer;

            if (def) {
                const name = (p.polymerName || "").trim();
                if (!name) {
                    err.polymerName = "Give the new polymer a short name, e.g. P(LLA-CL).";
                } else if (lowerNames.includes(name.toLowerCase())) {
                    err.polymerName = `${allNames[lowerNames.indexOf(name.toLowerCase())]} is already in the list: choose it there.`;
                } else if (name.length > 100) {
                    err.polymerName = "At most 100 characters.";
                }
                if (def.polymerType === "COPOLYMER" && (def.monomers || []).length < 2) {
                    err.definition = "A copolymer is made of at least two different monomers.";
                }
            } else if (!p.polymerName) {
                err.polymerName = "Choose a polymer";
            } else if (!allNames.includes(p.polymerName)) {
                err.polymerName = "Choose a polymer from the list";
            } else if (mode === "copolymer" && !isCopolymer(p)) {
                err.polymerName = `${p.polymerName} is not a copolymer. Choose "Single polymer" above, or a copolymer.`;
            } else if (mode === "single" && isCopolymer(p)) {
                err.polymerName = `${p.polymerName} is a copolymer: choose "Copolymer" above to give its composition.`;
            }

            if (isBlend) {
                if (isEmpty(p.weightRatio) || isNaN(p.weightRatio)) {
                    err.weightRatio = "Share is required";
                    hasWeightRatioError = true;
                } else if (p.weightRatio <= 0) {
                    err.weightRatio = "Share must be a positive number";
                    hasWeightRatioError = true;
                } else {
                    totalWeightRatio += Number(p.weightRatio);
                }
            }

            if (!isEmpty(p.polymerWeight) && Number(p.polymerWeight) <= 0) {
                err.polymerWeight = "Weight must be a positive number";
            }
            if (!isEmpty(p.molecularWeight) && Number(p.molecularWeight) <= 0) {
                err.molecularWeight = "Molecular weight must be a positive number";
            }

            // Copolymer composition: optional, but complete and summing to 100 when given
            const filled = (p.composition || []).filter(f => !isEmpty(f.fraction));
            if (filled.length > 0) {
                const monomers = monomersOf(p) || [];
                if (filled.length < monomers.length) {
                    err.composition = `Give a fraction for every monomer (${monomers.join(", ")}), or leave them all empty.`;
                } else if (filled.some(f => Number(f.fraction) <= 0 || Number(f.fraction) > 100)) {
                    err.composition = "Each fraction must be between 0 and 100.";
                } else {
                    const sum = filled.reduce((t, f) => t + Number(f.fraction), 0);
                    if (Math.abs(sum - 100) > 1) err.composition = `The composition adds up to ${sum}, not 100.`;
                }
            }

            newErrors.push(err);
        });

        let problem = null;
        if (isBlend && polymers.length < 2) {
            problem = "A blend has at least two polymers. Add another one, or choose Single polymer / Copolymer.";
        } else if (isBlend && !hasWeightRatioError) {
            const roundedTotal = Math.round(totalWeightRatio * 100) / 100;
            if (Math.abs(roundedTotal - 100) > 0.01) {
                newErrors.forEach(e => { e.weightRatio = `The shares add up to ${roundedTotal}, not 100.`; });
            }
        }
        if (isBlend) {
            const names = polymers.map(p => (p.polymerName || "").trim().toLowerCase()).filter(Boolean);
            if (new Set(names).size < names.length) {
                problem = "The same polymer is listed twice in the blend.";
            }
        }

        setErrors(newErrors);
        setFormError(problem);
        return !problem && !newErrors.some(err => Object.values(err).some(Boolean));
    };

    /** An all-empty composition is saved as "not given"; a defined polymer keeps a trimmed definition. */
    const cleaned = () => polymers.map(p => {
        const composition = (p.composition || []).filter(f => !isEmpty(f.fraction))
            .map(f => ({ monomer: f.monomer, fraction: Number(f.fraction) }));
        const out = { ...p, polymerName: (p.polymerName || "").trim() };
        if (mode !== "blend") out.weightRatio = 100;
        if (p.newPolymer) {
            const def = p.newPolymer;
            const copolymer = def.polymerType === "COPOLYMER";
            out.newPolymer = {
                polymerType: def.polymerType,
                architecture: copolymer && def.architecture ? def.architecture : null,
                monomers: copolymer ? def.monomers : [],
                fullName: def.fullName?.trim() || null,
                casNumber: def.casNumber?.trim() || null,
                note: def.note?.trim() || null,
            };
            out.copolymer = copolymer;
        } else {
            delete out.newPolymer;
        }
        return composition.length
            ? { ...out, composition, compositionUnit: p.compositionUnit || "mol%" }
            : { ...out, composition: null, compositionUnit: null };
    });

    const save = () => {
        if (validate()) {
            onSave(cleaned());
            onClose();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        save();
    };

    // Esc closes and Enter saves, except inside a dropdown (where they close its menu / pick an option).
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.target.id || "").startsWith("react-select")) return;
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            } else if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault();
                save();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    });

    const renderDefinition = (polymer, index) => {
        const def = polymer.newPolymer;
        const copolymer = def.polymerType === "COPOLYMER";
        return (
            <div className="col-12">
                <div className="p-3 rounded border border-primary-subtle bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="fw-bold text-primary">
                            <i className="bi bi-stars me-1"></i>
                            New {copolymer ? "copolymer" : "polymer"}
                            {polymer.pendingApproval && <span className="badge bg-warning text-dark ms-2">awaiting approval</span>}
                        </span>
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => choosePolymer(index, "")}>
                            Choose from the list instead
                        </button>
                    </div>
                    <div className="row g-3">
                        {mode === "blend" && (
                            <div className="col-12">
                                <div className="btn-group btn-group-sm" role="group" aria-label="Kind of new polymer">
                                    {[["HOMOPOLYMER", "Homopolymer"], ["COPOLYMER", "Copolymer"]].map(([value, label]) => (
                                        <button key={value} type="button"
                                            className={`btn ${def.polymerType === value ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => updateDefinition(index, { polymerType: value })}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="col-md-5">
                            <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`new-polymer-name-${index}`}>
                                Short name <span className="text-danger">*</span>
                            </label>
                            <input id={`new-polymer-name-${index}`} className={`form-control ${errors[index]?.polymerName ? "is-invalid" : ""}`}
                                value={polymer.polymerName || ""} maxLength={100}
                                placeholder={copolymer ? "e.g. P(LLA-CL)" : "e.g. PBAT"}
                                onChange={(e) => { updatePolymer(index, { polymerName: e.target.value }); clearError(index, "polymerName"); }} />
                        </div>
                        <div className="col-md-7">
                            <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`new-polymer-full-${index}`}>Full name</label>
                            <input id={`new-polymer-full-${index}`} className="form-control" value={def.fullName || ""}
                                placeholder={copolymer ? "e.g. Poly(L-lactide-co-ε-caprolactone)" : "e.g. Poly(butylene adipate-co-terephthalate)"}
                                onChange={(e) => updateDefinition(index, { fullName: e.target.value })} />
                        </div>
                        {errors[index]?.polymerName && (
                            <div className="col-12 text-danger small mt-1">
                                <i className="bi bi-exclamation-circle me-1"></i>{errors[index].polymerName}
                            </div>
                        )}
                        {copolymer ? (
                            <>
                                <div className="col-md-8">
                                    <label className="form-label small fw-bold text-secondary mb-1">
                                        Monomers, in the order you give the composition <span className="text-danger">*</span>
                                    </label>
                                    <CreatableSelect
                                        isMulti
                                        aria-label="Monomers"
                                        options={monomerOptions}
                                        value={(def.monomers || []).map(m => monomerOptions.find(o => o.value === m) || { value: m, label: m })}
                                        onChange={(selected) => updateDefinition(index, { monomers: (selected || []).map(s => s.value.trim()).filter(Boolean) })}
                                        formatCreateLabel={(text) => `Add monomer "${text}"`}
                                        placeholder="e.g. LLA, CL"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`new-polymer-arch-${index}`}>Architecture</label>
                                    <select id={`new-polymer-arch-${index}`} className="form-select" value={def.architecture || ""}
                                        onChange={(e) => updateDefinition(index, { architecture: e.target.value })}>
                                        {ARCHITECTURES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                    </select>
                                </div>
                                {errors[index]?.definition && (
                                    <div className="col-12 text-danger small">
                                        <i className="bi bi-exclamation-circle me-1"></i>{errors[index].definition}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="col-md-5">
                                <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`new-polymer-cas-${index}`}>CAS number</label>
                                <input id={`new-polymer-cas-${index}`} className="form-control" value={def.casNumber || ""}
                                    placeholder="e.g. 55231-08-8" onChange={(e) => updateDefinition(index, { casNumber: e.target.value })} />
                            </div>
                        )}
                        <div className="col-12">
                            <label className="form-label small fw-bold text-secondary mb-1" htmlFor={`new-polymer-note-${index}`}>Note for the reviewer</label>
                            <input id={`new-polymer-note-${index}`} className="form-control" value={def.note || ""}
                                placeholder="e.g. supplier and grade, or how it differs from similar entries"
                                onChange={(e) => updateDefinition(index, { note: e.target.value })} />
                        </div>
                    </div>
                    <div className="small text-muted mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        An administrator reviews new polymers. Until then this run is saved as <em>Needs update</em>;
                        once the polymer is approved it goes to review automatically.
                    </div>
                </div>
            </div>
        );
    };

    const renderComposition = (polymer, index) => {
        const monomers = monomersOf(polymer);
        if (!monomers) return null;
        const architecture = polymer.newPolymer ? polymer.newPolymer.architecture : polymerInfo[polymer.polymerName]?.architecture;
        if (polymer.newPolymer && monomers.length < 2) return null;
        return (
            <div className="col-12">
                <div className="p-3 rounded border bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold text-secondary">
                            Copolymer composition
                            {architecture && (
                                <span className="badge bg-light text-secondary border ms-2 text-lowercase">{architecture}</span>
                            )}
                        </span>
                        {monomers.length > 0 && (
                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                value={polymer.compositionUnit || "mol%"} aria-label="Composition unit"
                                onChange={(e) => updatePolymer(index, { compositionUnit: e.target.value })}>
                                <option value="mol%">mol%</option>
                                <option value="wt%">wt%</option>
                            </select>
                        )}
                    </div>
                    {monomers.length > 0 ? (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {monomers.map((m, mi) => (
                                <React.Fragment key={m}>
                                    {mi > 0 && <span className="text-muted fw-bold">:</span>}
                                    <div className="input-group input-group-sm" style={{ width: '8rem' }}>
                                        <span className="input-group-text">{m}</span>
                                        <input type="number" step="any" min="0" max="100"
                                            className={`form-control ${errors[index]?.composition ? "is-invalid" : ""}`}
                                            value={fractionOf(polymer, m)} aria-label={`${m} fraction`}
                                            onKeyDown={blockInvalidChars}
                                            onChange={(e) => handleCompositionChange(index, m, e.target.value)} />
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <div className="small text-muted">The monomers of this copolymer vary by grade, so no composition is recorded.</div>
                    )}
                    <div className="small text-muted mt-2">
                        The ratio of monomers inside this one polymer, e.g. 75:25. Optional, but it makes the record much more useful.
                    </div>
                    {errors[index]?.composition && (
                        <div className="text-danger small mt-1">
                            <i className="bi bi-exclamation-circle me-1"></i>{errors[index].composition}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const options = optionsFor(mode || "single");

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSubmit}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-layers-half me-2 text-primary"></i>
                            Polymer Configuration
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        <div className="mb-4">
                            <div className="fw-bold text-secondary mb-2">What is the polymer of this solution?</div>
                            <div className="row g-2" role="radiogroup" aria-label="Kind of polymer">
                                {MODES.map(m => (
                                    <div className="col-md-4" key={m.key}>
                                        <button type="button" role="radio" aria-checked={mode === m.key}
                                            className="cx-choice"
                                            onClick={() => changeMode(m.key)} disabled={!loaded}>
                                            <span className="cx-choice__title"><i className={`bi ${m.icon}`} aria-hidden="true"></i>{m.label}</span>
                                            <span className="cx-choice__hint">{m.hint}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {!loaded || !mode ? (
                            <div className="text-center p-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : (
                            <>
                                {polymers.map((polymer, index) => (
                                    <div key={index} className="card border-light shadow-sm mb-4">
                                        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                                            <span className="fw-bold text-primary">
                                                {mode === "blend" ? `Blend component ${index + 1}` : mode === "copolymer" ? "Copolymer" : "Polymer"}
                                            </span>
                                            {mode === "blend" && polymers.length > 2 && (
                                                <button type="button" className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                                    onClick={() => handleRemovePolymer(index)} title="Remove Polymer">
                                                    <i className="bi bi-trash me-1"></i> Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="card-body p-4">
                                            <div className="row g-4">
                                                {polymer.newPolymer ? renderDefinition(polymer, index) : (
                                                    <div className="col-12">
                                                        <label className="form-label fw-bold text-secondary">
                                                            {mode === "copolymer" ? "Copolymer" : "Polymer"} <span className="text-danger">*</span>
                                                        </label>
                                                        <Select
                                                            name="polymerName"
                                                            aria-label={mode === "copolymer" ? "Copolymer" : "Polymer"}
                                                            value={options.find(o => o.value === polymer.polymerName)
                                                                || (polymer.polymerName ? { value: polymer.polymerName, label: polymer.polymerName } : null)}
                                                            onChange={(selected) => choosePolymer(index, selected?.value || "")}
                                                            options={options}
                                                            styles={selectStyles}
                                                            menuPortalTarget={document.body}
                                                            placeholder={mode === "copolymer" ? "Search copolymers, e.g. PLGA…" : "Search polymers…"}
                                                            isSearchable
                                                            className={errors[index]?.polymerName ? "is-invalid" : ""}
                                                        />
                                                        {errors[index]?.polymerName && (
                                                            <div className="text-danger small mt-1">
                                                                <i className="bi bi-exclamation-circle me-1"></i>{errors[index].polymerName}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {renderComposition(polymer, index)}

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary">Weight (g)</label>
                                                    <input type="number" step="any" min="0" name="polymerWeight"
                                                        className={`form-control shadow-sm ${errors[index]?.polymerWeight ? "is-invalid" : ""}`}
                                                        value={polymer.polymerWeight ?? ""} onChange={(e) => handleNumberChange(index, e)}
                                                        onKeyDown={blockInvalidChars} placeholder="Input: 5" />
                                                    {errors[index]?.polymerWeight && (
                                                        <div className="text-danger small mt-1">
                                                            <i className="bi bi-exclamation-circle me-1"></i>{errors[index].polymerWeight}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary">Molecular Weight (kDa)</label>
                                                    <input type="number" step="any" min="0" name="molecularWeight"
                                                        className={`form-control shadow-sm ${errors[index]?.molecularWeight ? "is-invalid" : ""}`}
                                                        value={polymer.molecularWeight ?? ""} onChange={(e) => handleNumberChange(index, e)}
                                                        onKeyDown={blockInvalidChars} placeholder="Input: 150" />
                                                    {errors[index]?.molecularWeight && (
                                                        <div className="text-danger small mt-1">
                                                            <i className="bi bi-exclamation-circle me-1"></i>{errors[index].molecularWeight}
                                                        </div>
                                                    )}
                                                </div>

                                                {mode === "blend" && (
                                                    <div className="col-12">
                                                        <label className="form-label fw-bold text-secondary">
                                                            Share in the blend (wt%) <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="input-group">
                                                            <input type="number" step="any" min="0" name="weightRatio"
                                                                className={`form-control shadow-sm ${errors[index]?.weightRatio ? "is-invalid" : ""}`}
                                                                value={polymer.weightRatio ?? ""} onChange={(e) => handleNumberChange(index, e)}
                                                                onKeyDown={blockInvalidChars} placeholder="e.g. 50" />
                                                            <span className="input-group-text bg-white text-muted">wt%</span>
                                                        </div>
                                                        {errors[index]?.weightRatio && (
                                                            <div className="text-danger small mt-1">
                                                                <i className="bi bi-exclamation-circle me-1"></i>{errors[index].weightRatio}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {formError && (
                                    <div className="alert alert-danger py-2 small"><i className="bi bi-exclamation-circle me-1"></i>{formError}</div>
                                )}

                                {mode === "blend" ? (
                                    <button type="button" className="btn btn-outline-primary w-100 py-2" style={{ borderStyle: 'dashed' }}
                                        onClick={handleAddPolymer}>
                                        <i className="bi bi-plus-lg me-2"></i>
                                        Add another polymer to the blend
                                    </button>
                                ) : null}
                                <p className="small text-muted mt-2 mb-0">
                                    {mode === "copolymer"
                                        ? <>A copolymer such as PLGA or P(VDF-TrFE) is <strong>one</strong> polymer made of several monomers. To mix it with another polymer, choose <strong>Polymer blend</strong>.</>
                                        : mode === "blend"
                                            ? <>Each component can be a homopolymer or a copolymer (with its own composition); the shares add up to 100 wt%.</>
                                            : <>Made of two or more monomers? Choose <strong>Copolymer</strong>. Two polymers mixed? Choose <strong>Polymer blend</strong>.</>}
                                    {' '}Nanoparticles, salts and drugs go under <strong>Additives</strong>.
                                </p>
                            </>
                        )}
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill" disabled={!loaded}>
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
